import React, { useEffect } from 'react';
import PublicLayout from './components/layout/PublicLayout';
import AppLayout from './components/layout/AppLayout';
import AdminLayout from './components/layout/AdminLayout';
import { Routes, Route, Outlet } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { AuthProvider, useAuth } from './context/AuthContext';
import { QuotaNotificationProvider } from './contexts/QuotaNotificationContext';
import Footer from './components/Footer'
import LoadingIndicator from './components/common/LoadingIndicator'
import { ChatConfigWrapper } from './components/common/ChatConfigWrapper';
import './app/globals.css'

// Import your existing page components
import RootRedirect from './app/redirect/page';
import OAuthCallbackPage from './app/oauth/callback/page';
import DashboardPage from './app/dashboard/page'
import UserProfilePage from './app/user/profile/page';
import { ToastProvider } from '@/hooks/useToast';
import { AssistantActionProvider } from '@/contexts/AssistantActionContext';
import ConnectionPage from './app/user/connections/page'
import BillingPage from './app/billing/page';
import SettingsPage from './app/user/settings/page';
import LLMPage from './app/user/llm/page';
import ContentStrategyPage from './app/user/content-strategy/page';
import PublicArticlePage from './app/public/articles/page';
import ArticlesPage from './app/articles/page';
import ArticleCreatePage from './app/articles/create/page';
import ArticleEditPage from './app/articles/edit/page';
import ArticlePreviewPage from './app/articles/preview/page';
import AssetsPage from './app/assets/page';

// Admin pages
import AdminToolsPage from './app/admin-tools/page'
import AdminUsersPage from './app/admin-tools/users/page'
import AdminTenantsPage from './app/admin-tools/tenants/page'
import AdminSettingsPage from './app/admin-tools/settings/page'
import EmailLayout from './app/admin-tools/email/layout'
import EmailTemplatesPage from './app/admin-tools/email/page'
import EmailLogsPage from './app/admin-tools/email/logs/page'
import EmailSettingsPage from './app/admin-tools/email/settings/page'
import AdminMigrationPage from './app/admin-tools/articles/page'

// Auth pages
import ActivatePage from './app/auth/activate/page'
import ValidateEmailPage from './app/auth/validate-email/page'
import BlogSpotCallbackPage from './app/auth/blogspot/callback/page'
import WordPressCallbackPage from './app/auth/wordpress/callback/page'
import VerifyEmailRequiredPage from './app/verify-email-required/page'

// Admin test pages
import ImageGenerationTestPage from './app/admin-tools/image-generator/page'

// 404 page
import NotFoundWrapper from './app/404/NotFoundWrapper'

// Component to remove initial loader when auth is ready
function InitialLoaderRemover() {
  const auth = useAuth();

  useEffect(() => {
    // Remove initial loader only when auth check is complete
    if (!auth?.loading) {
      const initialLoader = document.getElementById('initial-loader');
      if (initialLoader) {
        initialLoader.style.opacity = '0';
        initialLoader.style.transition = 'opacity 0.3s ease-out';
        setTimeout(() => {
          initialLoader.remove();
        }, 300);
      }
    }
  }, [auth?.loading]);

  return null;
}

function App() {
  return (
    <AuthProvider>
      <QuotaNotificationProvider>
        <ToastProvider>
          <AssistantActionProvider>
            <ChatConfigWrapper>
              <div className="App min-h-screen flex flex-col">
                {/* Remove initial loader when auth is ready */}
                <InitialLoaderRemover />

                {/* Global Loading Indicator */}
                <LoadingIndicator />


                {/* Main Content */}
                <main className="flex-1">
                  <Routes>
                    {/* Public routes */}
                    <Route element={<PublicLayout />}>
                      <Route path="/" element={<RootRedirect />} />
                      <Route path="/oauth/callback/:platform" element={<OAuthCallbackPage />} />
                      <Route path="/auth/activate" element={<ActivatePage />} />
                      <Route path="/auth/validate-email" element={<ValidateEmailPage />} />
                      <Route path="/auth/blogspot/callback" element={<BlogSpotCallbackPage />} />
                      <Route path="/auth/wordpress/callback" element={<WordPressCallbackPage />} />
                      <Route path="/public/articles/:uuid" element={<PublicArticlePage />} />
                      <Route path="/verify-email-required" element={<VerifyEmailRequiredPage />} />
                    </Route>

                    {/* Protected routes */}
                    <Route element={<AppLayout />}>
                      <Route path="/dashboard" element={<DashboardPage />} />
                      <Route path="/articles" element={<ArticlesPage />} />
                      <Route path="/articles/create" element={<ArticleCreatePage />} />
                      <Route path="/articles/edit" element={<ArticleEditPage />} />
                      <Route path="/articles/preview" element={<ArticlePreviewPage />} />
                      <Route path="/assets" element={<AssetsPage />} />

                      {/* Settings routes */}
                      <Route path="/user/settings" element={<SettingsPage />} />
                      <Route path="/user/profile" element={<UserProfilePage />} />
                      <Route path="/user/connections" element={<ConnectionPage />} />
                      <Route path="/user/llm" element={<LLMPage />} />
                      <Route path="/user/content-strategy" element={<ContentStrategyPage />} />
                      <Route path="/billing" element={<BillingPage />} />
                    </Route>

                    {/* Admin routes */}
                    <Route element={<AdminLayout />}>
                      <Route path="/admin-tools" element={<AdminToolsPage />} />
                      <Route path="/admin-tools/users" element={<AdminUsersPage />} />
                      <Route path="/admin-tools/tenants" element={<AdminTenantsPage />} />
                      <Route path="/admin-tools/settings" element={<AdminSettingsPage />} />
                      <Route path="/admin-tools/articles" element={<AdminMigrationPage />} />

                      {/* Email System Routes */}
                      <Route path="/admin-tools/email" element={<EmailLayout><Outlet /></EmailLayout>}>
                        <Route index element={<EmailTemplatesPage />} />
                        <Route path="logs" element={<EmailLogsPage />} />
                        <Route path="settings" element={<EmailSettingsPage />} />
                      </Route>

                      {/* Admin test tools - only available in development mode */}
                      {import.meta.env.DEV && (
                        <Route path="/admin-tools/image-generator" element={<ImageGenerationTestPage />} />
                      )}
                    </Route>

                    {/* 404 Catch-all route - must be last */}
                    <Route path="*" element={<NotFoundWrapper />} />
                  </Routes>
                </main>

                {/* Footer */}
                <Footer />

                {/* Toast notifications */}
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 4000,
                    style: {
                      background: '#363636',
                      color: '#fff',
                    },
                  }}
                />
              </div>
            </ChatConfigWrapper>
          </AssistantActionProvider>
        </ToastProvider>
      </QuotaNotificationProvider>
    </AuthProvider>
  )
}

export default App
